import type { ReceiptPrintData } from '../types';
import { ReceiptQrCode } from './ReceiptQrCode';
import { VerticalText } from './VerticalText';
import { PAYMENT_METHOD_LABELS, formatMoney, formatReceiptDate, formatPlainDate } from '../utils/receiptFormat';
import { getAffiliation, AFFILIATION_CATEGORY_CODE } from '../utils/affiliation';

interface Props {
  data: ReceiptPrintData;
}

/** US Letter layout with the vertical "RECIBO OFICIAL" sidebar, matching RECIBO.pdf. */
export function LetterReceiptPrint({ data }: Props) {
  const numberStr = String(data.number).padStart(6, '0');
  const isVoid = data.status === 'void';
  const cur = data.currency === 'USD' ? 'USD' : 'Bs.';
  const affiliation = getAffiliation(data);

  return (
    <div
      className="relative flex overflow-hidden bg-white text-black"
      style={{ width: '8.5in', minHeight: '10.96in', fontFamily: 'Arial, Helvetica, sans-serif' }}
    >
      {isVoid && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center"
        >
          <span
            className="select-none font-bold tracking-widest text-rose-700/20"
            style={{ transform: 'rotate(-30deg)', fontSize: '120px' }}
          >
            ANULADO
          </span>
        </div>
      )}
      {/* White margin before the sidebar — the olive stripe does not start
          flush at the page edge. */}
      <div className="shrink-0" style={{ width: '0.35in' }} />

      {/* Vertical sidebar: olive block, small gap, olive text on white sized
          to the stripe's width, small gap, olive block — matches the
          reference letterhead. Text starts near the page's vertical midpoint. */}
      <div className="flex shrink-0 flex-col" style={{ width: '0.42in' }}>
        <div className="w-full bg-[#8a8a70]" style={{ height: '1.5in' }} />
        <div style={{ height: '0.15in' }} />
        <VerticalText
          width="100%"
          height="3.3in"
          className="font-light tracking-wide text-[#8a8a70]"
          style={{ fontSize: '38px' }}
        >
          RECIBO OFICIAL
        </VerticalText>
        <div style={{ height: '0.15in' }} />
        <div className="w-full flex-1 bg-[#8a8a70]" />
      </div>

      {/* Faint "80 años" watermark. A transparent logo at low opacity keeps the page white
          (an opaque paper texture would tint the whole sheet when printed). */}
      <img
        src="/receipt/logo-80.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 select-none"
        style={{ width: '5.5in', transform: 'translate(-45%, -50%)', opacity: 0.07, filter: 'grayscale(1) sepia(0.4)' }}
      />

      {/* Content column: the footer is pushed to the bottom of the page with mt-auto. */}
      <div className="relative z-[1] flex flex-1 flex-col" style={{ padding: '0.5in' }}>
        {/* Header: shield + name on the left; number with the 80-años mark below it on the right */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img src="/receipt/shield.png" alt="Las Asambleas de Dios de Bolivia" style={{ width: '0.95in', height: '0.95in' }} />
            <div>
              <p className="text-xs font-bold uppercase tracking-wide">
                Las Asambleas de Dios de Bolivia
              </p>
              <p className="text-[10px] text-gray-600">Construyendo el futuro, honrando la historia</p>
              <p className="text-[10px] text-gray-600">R.S. 101452/61 - 215651/95</p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <p className="text-xl font-bold text-rose-700">N° {numberStr}</p>
            {isVoid && <p className="mt-1 text-sm font-bold text-rose-700">ANULADO</p>}
            <img src="/receipt/logo-80.png" alt="80 años" className="mt-1" style={{ width: '1.1in' }} />
          </div>
        </div>

        <h1 className="mt-6 text-center text-2xl font-bold uppercase tracking-wide">
          Recibo Oficial
        </h1>
        {/* Plain receipts carry no subtitle; only special categories name themselves. */}
        {data.categoryCode === AFFILIATION_CATEGORY_CODE && (
          <p className="text-center text-sm text-gray-500">{data.categoryName}</p>
        )}

        {/* Payer info */}
        <div className="mt-6 flex justify-between text-sm">
          <div>
            <span className="font-semibold">Fecha: </span>
            {formatReceiptDate(data.issuedAt)}
          </div>
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <div>
            <span className="font-semibold">Recibí de: </span>
            {data.payerName}
          </div>
          {data.payerCi && (
            <div>
              <span className="font-semibold">C.I.: </span>
              {data.payerCi}
            </div>
          )}
        </div>

        {/* Line items table */}
        <table className="mt-6 w-full border-collapse text-sm">
          <thead>
            <tr className="border-b-2 border-black">
              <th className="py-1.5 text-left font-semibold">Concepto</th>
              <th className="py-1.5 text-center font-semibold">Cantidad</th>
              <th className="py-1.5 text-left font-semibold">Descripción</th>
              <th className="py-1.5 text-right font-semibold">Precio Unitario</th>
              <th className="py-1.5 text-right font-semibold">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.lineNo} className="border-b border-gray-300">
                <td className="py-1.5">{line.conceptName ?? '—'}</td>
                <td className="py-1.5 text-center">{line.quantity}</td>
                <td className="py-1.5">{line.description}</td>
                <td className="py-1.5 text-right">{formatMoney(line.unitPrice)}</td>
                <td className="py-1.5 text-right">{formatMoney(line.subtotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="mt-4 flex items-start justify-between">
          <div className="max-w-xs text-sm">
            <span className="font-semibold">Son: </span>
            {data.totalInWords}
          </div>
          <div className="w-56 text-sm">
            <div className="flex justify-between border-b border-gray-300 py-1">
              <span>Subtotal {cur}</span>
              <span>{formatMoney(data.subtotal)}</span>
            </div>
            <div className="flex justify-between border-b border-gray-300 py-1">
              <span>Descuento {cur}</span>
              <span>{formatMoney(data.discount)}</span>
            </div>
            {data.payments.map((p, i) => (
              <div key={i} className="flex justify-between border-b border-gray-300 py-1">
                <span>{PAYMENT_METHOD_LABELS[p.method]} {cur}</span>
                <span>{formatMoney(p.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between py-1 font-bold">
              <span>Monto a pagar {cur}</span>
              <span>{formatMoney(data.total)}</span>
            </div>
            {data.currency === 'USD' && data.exchangeRate && (
              <p className="text-right text-[10px] text-gray-500">T/C: {data.exchangeRate}</p>
            )}
          </div>
        </div>

        {affiliation && (
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 rounded border border-gray-400 px-3 py-2 text-sm">
            <div><span className="font-semibold">Fecha de afiliación: </span>{affiliation.affiliation_date ? formatPlainDate(affiliation.affiliation_date) : '—'}</div>
            <div><span className="font-semibold">Código ADB: </span>{affiliation.adb_code || '—'}</div>
            <div><span className="font-semibold">Distrito: </span>{affiliation.district || '—'}</div>
            <div><span className="font-semibold">Región: </span>{affiliation.region || '—'}</div>
          </div>
        )}

        {isVoid && data.voidReason && (
          <p className="mt-4 text-sm text-rose-700">
            <span className="font-semibold">Motivo de anulación:</span> {data.voidReason}
          </p>
        )}

        {/* Footer: dotted rule, QR note, then the contact strip at the very bottom */}
        <div className="mt-auto pt-10">
          <div className="border-t border-dashed border-gray-400 pt-3">
            <div className="flex items-end justify-between gap-4">
              <p className="max-w-md text-[10px] text-gray-500">
                Este documento es la representación gráfica del documento emitido por el sistema
                integral de Las Asambleas de Dios de Bolivia y puede ser descargado escaneando el
                siguiente código QR.
              </p>
              <ReceiptQrCode url={data.publicUrl} size={80} />
            </div>
            <img src="/receipt/redes.png" alt="Calle Ballivián N° 1497 · addbolivia@yahoo.es · 73510370 · @ADBolOficial" className="mx-auto mt-2" style={{ width: '4.2in' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
